import { environment } from '../../../environments/environment';
import type { SmartDiscoveryMockTimingConfig } from '../../../environments/environment.model';

/** sessionStorage JSON overrides `environment.mockApiTiming.smartDiscovery` (devtools only). */
export const DISCOVERY_MOCK_TIMING_STORAGE_KEY = 'momentum.mock.discovery.timing';

export interface ResolvedDiscoveryMockTiming {
  pollResponseDelayMs: number;
  signalsExtractingProgressPolls: number;
  summaryReadyAfterProgressPolls: number;
  replayOnEachBind: boolean;
}

const DEFAULTS: ResolvedDiscoveryMockTiming = {
  pollResponseDelayMs: 800,
  signalsExtractingProgressPolls: 5,
  summaryReadyAfterProgressPolls: 8,
  replayOnEachBind: true,
};

function coerceTiming(
  partial: SmartDiscoveryMockTimingConfig | null | undefined,
): ResolvedDiscoveryMockTiming {
  const pollResponseDelayMs =
    typeof partial?.pollResponseDelayMs === 'number' && partial.pollResponseDelayMs >= 0
      ? partial.pollResponseDelayMs
      : DEFAULTS.pollResponseDelayMs;
  const signalsExtractingProgressPolls =
    typeof partial?.signalsExtractingProgressPolls === 'number' &&
    partial.signalsExtractingProgressPolls >= 1
      ? Math.floor(partial.signalsExtractingProgressPolls)
      : DEFAULTS.signalsExtractingProgressPolls;
  let summaryReadyAfterProgressPolls =
    typeof partial?.summaryReadyAfterProgressPolls === 'number' &&
    partial.summaryReadyAfterProgressPolls >= 1
      ? Math.floor(partial.summaryReadyAfterProgressPolls)
      : DEFAULTS.summaryReadyAfterProgressPolls;
  if (summaryReadyAfterProgressPolls <= signalsExtractingProgressPolls) {
    summaryReadyAfterProgressPolls = signalsExtractingProgressPolls + 2;
  }
  const replayOnEachBind =
    typeof partial?.replayOnEachBind === 'boolean'
      ? partial.replayOnEachBind
      : DEFAULTS.replayOnEachBind;

  return {
    pollResponseDelayMs,
    signalsExtractingProgressPolls,
    summaryReadyAfterProgressPolls,
    replayOnEachBind,
  };
}

function readSessionOverride(): SmartDiscoveryMockTimingConfig | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(DISCOVERY_MOCK_TIMING_STORAGE_KEY);
    if (!raw?.trim()) {
      return null;
    }
    return JSON.parse(raw) as SmartDiscoveryMockTimingConfig;
  } catch {
    return null;
  }
}

/** Effective discovery intelligence mock timing: defaults ← environment ← sessionStorage. */
export function getDiscoveryMockTiming(): ResolvedDiscoveryMockTiming {
  return coerceTiming({
    ...DEFAULTS,
    ...environment.mockApiTiming?.smartDiscovery,
    ...readSessionOverride(),
  });
}
